import { Component, OnInit } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TeamsService } from '@entscheidungsnavi/api-client';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { TeamInviteInfoDto } from '@entscheidungsnavi/api-types';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModalContentComponent } from '@entscheidungsnavi/widgets';
import { SharedModule } from '../../../../modules/shared/shared.module';

export interface InvitesModalResult {
  hasJoinedATeam: boolean;
  openInviteTokens: string[];
}

@Component({
  selector: 'dt-invites-modal',
  templateUrl: './invites-modal.component.html',
  styleUrls: ['./invites-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    ModalContentComponent,
    SharedModule,
  ],
})
export class InvitesModalComponent implements OnInit {
  displayedColumns: string[] = ['teamName', 'inviterName', 'date', 'actions'];
  dataSource = new MatTableDataSource<TeamInviteInfoDto>();
  hasJoinedATeam = false;

  constructor(
    private teamsService: TeamsService,
    private dialogRef: MatDialogRef<InvitesModalComponent, InvitesModalResult>,
    private snackbarService: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.teamsService.getInvitesToUser().subscribe({
      next: (invites: TeamInviteInfoDto[]) => {
        this.dataSource.data = invites;
      },
      error: () => {
        this.showSnackbar($localize`Fehler beim Laden der Einladungen.`);
      },
    });
  }

  async joinTeam(inviteToken: string): Promise<void> {
    try {
      const teamInfo = await firstValueFrom(this.teamsService.join(inviteToken));
      this.showSnackbar($localize`Du bist dem Team "${teamInfo.name}" beigetreten.`);
      this.dataSource.data = this.dataSource.data.filter(invite => invite.inviteToken !== inviteToken);
      this.hasJoinedATeam = true;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        this.showSnackbar($localize`Du bist bereits Teil dieses Teams.`);
      } else {
        this.showSnackbar($localize`Beim Teambeitritt ist ein unbekannter Fehler aufgetreten.`);
      }
    }
  }

  async rejectTeam(inviteToken: string): Promise<void> {
    try {
      await firstValueFrom(this.teamsService.rejectInvite(inviteToken));
      this.showSnackbar($localize`Einladung abgelehnt.`);
      this.dataSource.data = this.dataSource.data.filter(invite => invite.inviteToken !== inviteToken);
    } catch {
      this.showSnackbar($localize`Beim Ablehnen der Einladung ist ein unbekannter Fehler aufgetreten.`);
    }
  }

  close(): void {
    const openInviteTokens = this.dataSource.data.map(invite => invite.inviteToken);
    this.dialogRef.close({
      hasJoinedATeam: this.hasJoinedATeam,
      openInviteTokens,
    });
  }

  private showSnackbar(message: string): void {
    this.snackbarService.open(message, $localize`Ok`, { duration: 3000 });
  }
}
